import React from 'react';
import PropTypes from 'prop-types';
import { Link, Icon } from '@resy/ui-kitchen';
import { handleMktAnalytics } from '../AmexUtils';
import { ACQUISITION_CTA_EVENT_NAME } from '../constants';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import ExternalLink from '../../../../../../ui-kitchen/src/svg/icons/external-link.svg';
import './AmexNAA.scss';

const AmexNAA = ({
  awsFolderName,
  baseRequestUrl,
  matchMedia,
  sectionConfig,
  eventAnalytics,
}) => {
  const sectionId = sectionConfig?.section_id || sectionConfig?.id;
  const logo = sectionConfig?.logo || '';
  const title = sectionConfig?.title || '';
  const copy = sectionConfig?.copy || '';
  const terms = sectionConfig?.fine_print || '';
  const ctaLabel = sectionConfig?.cta?.label;
  const ctaUrl = sectionConfig?.cta?.url || '#';
  const titleCopy = sectionConfig?.title_copy || '';
  const heroImage = sectionConfig?.hero_image || '';
  const desktopImg = sectionConfig?.image?.desktop || {};
  const mobileImg = sectionConfig?.image?.mobile || {};
  const ariaLabel = sectionConfig?.external_link_aria;

  const hasHeroImage = Boolean(heroImage?.desktop && heroImage?.mobile);
  const hasDesktopImg = Boolean(desktopImg?.regular && desktopImg.retina);
  const hasMobileImg = Boolean(mobileImg.regular && mobileImg.retina);
  const imagePrefix = `${baseRequestUrl}${awsFolderName}/images/`;
  const logoPrefix = `${baseRequestUrl}${awsFolderName}/logos/`;
  const sanitizedTitle = sanitizeString(title);
  const sanitizedCopy = sanitizeString(copy);
  const sanitizedTerms = sanitizeString(terms);
  const sanitizedTitleCopy = sanitizeString(titleCopy);

  const renderContent = () => (
    <div className="AmexNAA__panelBody">
      {logo && (
        <img src={`${logoPrefix}${logo}`} className="AmexNAA__logo" alt="American Express and Resy logo" />
      )}
      {titleCopy && (
        <p
          className="AmexNAA__titleCopy"
          dangerouslySetInnerHTML={{ __html: sanitizedTitleCopy }} // eslint-disable-line react/no-danger
        />
      )}
      <h2
        id={sectionId}
        className="AmexNAA__title"
        dangerouslySetInnerHTML={{ __html: sanitizedTitle }} // eslint-disable-line react/no-danger
      />
      {copy && (
        <p
          className="AmexNAA__copy"
          dangerouslySetInnerHTML={{ __html: sanitizedCopy }} // eslint-disable-line react/no-danger
        />
      )}
      {terms && (
        <p
          className="AmexNAA__terms"
          dangerouslySetInnerHTML={{ __html: sanitizedTerms }} // eslint-disable-line react/no-danger
        />
      )}
      {ctaLabel && ctaUrl && (
        <Link
          className="AmexNAA__cta"
          href={ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            handleMktAnalytics(eventAnalytics, ACQUISITION_CTA_EVENT_NAME, {
              action_name: 'NAA - Click',
            });
          }}
        >
          {ctaLabel}
          <Icon component={ExternalLink} ariaLabel={ariaLabel} />
        </Link>
      )}
    </div>
  );

  return (
    <section className="AmexNAA" aria-labelledby={sectionId}>
      <div className="container">
        {!matchMedia.lg ? (
          <div className="AmexNAA__mobileWrapper">
            {renderContent()}
            {(hasMobileImg || hasHeroImage) && (
              <img
                className="AmexNAA__mobileImage"
                src={hasHeroImage ? `${imagePrefix}${heroImage.mobile}` : `${imagePrefix}${mobileImg.regular}`}
                srcSet={hasHeroImage ? `${imagePrefix}${heroImage.mobile}` : `${imagePrefix}${mobileImg.regular} 1x, ${imagePrefix}${mobileImg.retina} 2x`}
                alt=""
              />
            )}
          </div>
        ) : (
          <div className="AmexNAA__heroBackground">
            {(hasDesktopImg || hasHeroImage) && (
              <div className="AmexNAA__heroImageLayer">
                <img
                  className="AmexNAA__heroPicture"
                  src={hasHeroImage ? `${imagePrefix}${heroImage.desktop}` : `${imagePrefix}${desktopImg.regular}`}
                  srcSet={hasHeroImage ? `${imagePrefix}${heroImage.desktop}` : `${imagePrefix}${desktopImg.regular} 1x, ${imagePrefix}${desktopImg.retina} 2x`}
                  alt=""
                />
              </div>
            )}
            {renderContent()}
          </div>
        )}
      </div>
    </section>
  );
};

AmexNAA.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  sectionConfig: PropTypes.shape({
    section_id: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    title_copy: PropTypes.string,
    copy: PropTypes.string,
    logo: PropTypes.string,
    fine_print: PropTypes.string,
    external_link_aria: PropTypes.string,
    hero_image: PropTypes.shape({
      desktop: PropTypes.string,
      mobile: PropTypes.string,
    }),
    image: PropTypes.shape({
      desktop: PropTypes.shape({
        regular: PropTypes.string,
        retina: PropTypes.string,
      }),
      mobile: PropTypes.shape({
        regular: PropTypes.string,
        retina: PropTypes.string,
      }),
    }),
    cta: PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    }),
  }).isRequired,
  matchMedia: PropTypes.shape({
    lg: PropTypes.bool,
  }).isRequired,
  eventAnalytics: PropTypes.func,
};

AmexNAA.defaultProps = {
  eventAnalytics: null,
};

export default AmexNAA;
